import {
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Pagination,
  Paper,
  Skeleton,
  TablePagination,
  Typography,
  useTheme,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import React from "react";
import { IMobileRenderTableV2Props } from "./RenderMobileTableV2.types";
import { SelectAll } from "../../../Table/components/SelectAll/SelectAll";
import { CustomCheckbox } from "../../../formsComponents";
import { TablePaginationActions } from "../../../Table/TablePaginationActions";

export const CustomMobileTableV2: React.FC<IMobileRenderTableV2Props> = (
  props,
) => {
  const {
    rows,
    mobileColumns,
    isDataLoading,
    loaderSkeletonRows,
    totalRecords,
    uniqueRowKeyName,
    isPagination,
    extraFetchFactor,
    rowsPerPageOptions,
    onRowsPerPageChange,
    selectType,
    selectionMode,
    selectedRows,
    pageNumber = 1,
    onPageChange,
    onSelectionAllRows,
    onSelectRow,
  } = props;

  const [page, setPage] = React.useState(pageNumber);
  const [rowsPerPage, setRowsPerPage] = React.useState(rowsPerPageOptions);
  const theme = useTheme();

  const currentPageItems =
    !totalRecords && rowsPerPage > 0 && isPagination && !extraFetchFactor
      ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : rows;

  const handlePageChange = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number,
  ) => {
    if (onPageChange) {
      onPageChange(newPage + 1);
    }
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const rowsPerPage = parseInt(event.target.value, 10);
    if (onRowsPerPageChange) {
      onRowsPerPageChange(
        1,
        rowsPerPage === -1 ? totalRecords || rows.length : rowsPerPage,
      );
    }
    setRowsPerPage(rowsPerPage);
    setPage(0);
  };

  React.useEffect(() => {
    setPage(pageNumber - 1);
  }, [pageNumber]);

  React.useEffect(() => {
    setRowsPerPage(rowsPerPageOptions);
  }, [rowsPerPageOptions]);

  if (isDataLoading) {
    return <CardSkeleton loaderSkeletonRows={loaderSkeletonRows} />;
  }

  return (
    <>
      {selectionMode === "multiple" && (
        <Card
          sx={{
            border: 0,
            borderRadius: "5px",
            boxShadow: "rgba(0, 0, 0, 0.1) 0px 2px 3px",

            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" alignItems="center">
              <SelectAll
                checkedType={selectType}
                onChange={onSelectionAllRows}
              />
              <Typography variant="body2" sx={{ ml: 1 }}>
                Select All
              </Typography>
            </Box>
            {(selectedRows.length > 0 || selectType === "AllPages") && (
              <Typography variant="body2">
                {`(${
                  selectType === "AllPages" ? totalRecords : selectedRows.length
                } Rows Selected)`}
              </Typography>
            )}
          </Box>
        </Card>
      )}

      {currentPageItems.map((row, index) => (
        <Card
          key={row[uniqueRowKeyName] || index}
          sx={{
            border: "1px solid #ddd",
            borderRadius: "5px",
            boxShadow: "rgba(0, 0, 0, 0.1) 0px 2px 3px",
            backgroundColor: "grey.100",
            mt: 2,
          }}
        >
          <CardContent>
            {selectionMode !== "none" && (
              <CustomCheckbox
                disabled={selectType === "AllPages"}
                checked={
                  selectedRows.findIndex(
                    (x) => x[uniqueRowKeyName] === row[uniqueRowKeyName],
                  ) > -1 || selectType === "AllPages"
                }
                onChange={onSelectRow(row)}
              />
            )}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {mobileColumns.map((column, colIndex) => (
                  <Box key={`${row[uniqueRowKeyName]}-${colIndex}`}>
                    {/* Heading Section */}
                    {column.headingColumn && (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        marginBottom={2}
                      >
                        <Typography variant="h5" fontWeight="bold">
                          {row[column.headingColumn.fieldName] ?? ""}
                        </Typography>
                        {column.rightHeadingColumn && (
                          <Typography variant="body1" color="red">
                            {row[column.rightHeadingColumn.fieldName] ?? ""}
                          </Typography>
                        )}
                      </Box>
                    )}
                    {/* Subsection Section */}
                    {column.subSectionColumn && (
                      <Box marginBottom={2}>
                        {column.subSectionColumn.map((subColumn) => (
                          <Box key={subColumn.key} marginBottom={1}>
                            <Typography variant="body2" color="textSecondary">
                              {subColumn.onRowCellRender
                                ? subColumn.onRowCellRender(
                                    row[subColumn.fieldName],
                                    row,
                                  )
                                : subColumn.onRowCellValueRender
                                  ? subColumn.onRowCellValueRender(
                                      row[subColumn.fieldName],
                                      row,
                                    )
                                  : row[subColumn.fieldName]}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* Other Columns Section */}
                    {column.otherColumns && (
                      <Box marginBottom={2}>
                        {column.otherColumns.map((otherColumn) => (
                          <Box
                            key={otherColumn.key}
                            marginBottom={1}
                            display="flex"
                            justifyContent="space-between"
                          >
                            <Typography variant="body2" fontWeight="bold">
                              {otherColumn.headerName}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {otherColumn.onRowCellRender
                                ? otherColumn.onRowCellRender(
                                    row[otherColumn.fieldName],
                                    row,
                                  )
                                : otherColumn.onRowCellValueRender
                                  ? otherColumn.onRowCellValueRender(
                                      row[otherColumn.fieldName],
                                      row,
                                    )
                                  : row[otherColumn.fieldName]}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                    {/* Actions Section */}
                    {/* {column.actions && (
											<Box
												display="flex"
												justifyContent="flex-end"
												gap={1}
											>
												{column.actions.map((action) => (
													<Button
														key={action.name}
														variant="outlined"
														onClick={() => action.click(row)}
														sx={{
															display: "flex",
															flexDirection: "column",
															alignItems: "center",
														}}
													>
														{action.icon}
														<Typography variant="caption">
															{action.name}
														</Typography>
													</Button>
												))}
											</Box>
										)} */}

                    {column.actions && (
                      <Box
                        display="flex"
                        justifyContent="center"
                        flexWrap="wrap"
                        gap={1}
                        sx={{
                          maxWidth: "100%",

                          marginLeft: "auto",
                          "& > :nth-of-type(n+4)": {
                            marginTop: 1,
                          },
                        }}
                      >
                        {column.actions.map((action, index) => (
                          <Button
                            key={action.name}
                            variant="outlined"
                            onClick={() => action.click(row)}
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              order: index,
                            }}
                          >
                            {action.icon}
                            <Typography variant="caption">
                              {action.name}
                            </Typography>
                          </Button>
                        ))}
                      </Box>
                    )}
                  </Box>
                ))}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ))}

      {/* {currentPageItems.map((row, index) => (
				<Paper
					key={row[uniqueRowKeyName] || index}
					variant="outlined"
					sx={{
						borderRadius: "5px",

						p: 0,
						mt: 2,
					}}
				>
					<Box sx={{ m: 0, p: "0px !Important" }}>
						{selectionMode !== "none" && (
							<CustomCheckbox
								disabled={selectType === "AllPages"}
								checked={
									selectedRows.findIndex(
										(x) => x[uniqueRowKeyName] === row[uniqueRowKeyName]
									) > -1 || selectType === "AllPages"
								}
								onChange={onSelectRow(row)}
							/>
						)}
						{mobileColumns.map((column, colIndex) => (
							<Box key={`${row[uniqueRowKeyName]}-${colIndex}`}>
								<Box p={2}>
									{column.headingColumn && (
										<Box
											display="flex"
											justifyContent="space-between"
											alignItems="center"
											marginBottom={2}
										>
											<Typography
												variant="h5"
												fontWeight="bold"
											>
												{row[column.headingColumn.fieldName] ?? ""}
											</Typography>
											{column.rightHeadingColumn && (
												<Typography
													variant="body1"
													color="red"
												>
													{row[column.rightHeadingColumn.fieldName] ?? ""}
												</Typography>
											)}
										</Box>
									)}

									{column.subSectionColumn && (
										<Box marginBottom={2}>
											{column.subSectionColumn.map((subColumn) => (
												<Box
													key={subColumn.key}
													marginBottom={1}
												>
													<Typography
														variant="body2"
														color="textSecondary"
													>
														{subColumn.onRowCellRender
															? subColumn.onRowCellRender(
																	row[subColumn.fieldName],
																	row
															  )
															: subColumn.onRowCellValueRender
															? subColumn.onRowCellValueRender(
																	row[subColumn.fieldName],
																	row
															  )
															: row[subColumn.fieldName]}
													</Typography>
												</Box>
											))}
										</Box>
									)}

									{column.otherColumns && (
										<Box marginBottom={2}>
											{column.otherColumns.map((otherColumn) => (
												<Box
													key={otherColumn.key}
													marginBottom={1}
													display="flex"
													justifyContent="space-between"
												>
													<Typography
														variant="body2"
														fontWeight="bold"
													>
														{otherColumn.headerName}
													</Typography>
													<Typography
														variant="body2"
														color="textSecondary"
													>
														{otherColumn.onRowCellRender
															? otherColumn.onRowCellRender(
																	row[otherColumn.fieldName],
																	row
															  )
															: otherColumn.onRowCellValueRender
															? otherColumn.onRowCellValueRender(
																	row[otherColumn.fieldName],
																	row
															  )
															: row[otherColumn.fieldName]}
													</Typography>
												</Box>
											))}
										</Box>
									)}
								</Box>

								<Divider />
								{column.actions && (
									<Stack
										direction={"row"}
										p={1.5}
										pl={2}
										pr={2}
										flexWrap={"wrap"}
										alignItems={"center"}
										justifyContent={"space-between"}
										spacing={1}
									>
										{column.actions.map((action) => (
											<Stack
												direction={"row"}
												spacing={1}
												alignItems={"center"}
												key={action.name}
												sx={{
													svg: {
														color: theme.palette.primary.main,
														fontSize: "24px",
													},
												}}
												onClick={() => action.click(row)}
											>
												{action.icon}
												<Typography
													variant="body1"
													fontWeight={500}
													fontSize={"1rem"}
												>
													{action.name}
												</Typography>
											</Stack>
										))}
									</Stack>
								)}
							</Box>
						))}
					</Box>
				</Paper>
			))} */}

      {isPagination && (
        <Stack>
          <Stack direction={"row"} justifyContent={"start"}>
            <TablePagination
              rowsPerPageOptions={props.paginationList}
              sx={{
                borderBottom: 0,
                "& .MuiToolbar-root": {
                  flexWrap: "wrap !important",
                },
              }}
              count={totalRecords || rows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              SelectProps={{
                native: true,
              }}
              labelDisplayedRows={({ from, to }) => {
                if (extraFetchFactor) {
                  const count = rows.length - extraFetchFactor;
                  if (count < -1) {
                    return "Loading...";
                  }
                  if (rows.length === extraFetchFactor) {
                    return `${rows.length} rows currently shown`;
                  }
                  if (rows.length <= rowsPerPage) {
                    return `${rows.length} rows currently shown`;
                  }
                  return `${
                    rows.length - extraFetchFactor
                  } rows currently shown`; // Show range only
                }
                return `${from}-${to} of ${totalRecords || rows.length}`; // Include total when available
              }}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              ActionsComponent={(props) => (
                <TablePaginationActions
                  {...props}
                  extraFetchFactor={extraFetchFactor}
                />
              )}
              nextIconButtonProps={{
                disabled: extraFetchFactor
                  ? rows.length <= rowsPerPage // Enhanced logic
                  : totalRecords !== undefined
                    ? page >= Math.ceil(totalRecords / rowsPerPage) - 1
                    : page >= Math.ceil((rows.length as any) / rowsPerPage) - 1, // Default logic
              }}
            />
          </Stack>
        </Stack>
      )}
    </>
  );
};

export const CardSkeleton: React.FC<{ loaderSkeletonRows: number }> = (
  props,
) => {
  const { loaderSkeletonRows } = props;
  const rows = Array.from(Array(loaderSkeletonRows), (_, i) => `row-${i + 1}`);
  return (
    <>
      {rows.map((row) => {
        return (
          <Card key={row}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Skeleton variant="circular" width={50} height={50} />
                </Grid>
                <Grid item xs={10}>
                  <>
                    <Skeleton />
                    <Skeleton />
                    <Skeleton />
                    <Skeleton />
                  </>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        );
      })}
    </>
  );
};
